import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Form,
  InputGroup,
  Modal,
  Toast,
  ToastContainer,
  Spinner,
  Pagination,
  Alert,
} from 'react-bootstrap';
import {
  People,
  Search,
  PlusCircle,
  Pencil,
  Trash,
  CheckCircle,
  XCircle,
  InfoCircle,
  Person,
  Envelope,
  Telephone,
  Shield,
  ChevronDown,
  ChevronUp,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/AuthContext';

// Données fictives des utilisateurs (inchangées)
const mockUsers = [
  { id: 1, name: 'Jean Dupont', email: 'jean.dupont@email.ga', role: 'patient', status: 'active', phone: '+241 01 23 45 67', lastLogin: '2025-03-15' },
  { id: 2, name: 'Dr. Nzogho', email: 'dr.nzogho@cnamgs.ga', role: 'doctor', status: 'active', phone: '+241 01 23 45 68', lastLogin: '2025-03-14' },
  { id: 3, name: 'Pharmacie Centrale', email: 'pharmacy@cnamgs.ga', role: 'pharmacist', status: 'active', phone: '+241 01 23 45 69', lastLogin: '2025-03-13' },
  { id: 4, name: 'Admin CNAMGS', email: 'admin@cnamgs.ga', role: 'admin', status: 'active', phone: '+241 01 23 45 70', lastLogin: '2025-03-15' },
  { id: 5, name: 'Marie Nzogho', email: 'marie.nzogho@email.ga', role: 'patient', status: 'inactive', phone: '+241 01 23 45 71', lastLogin: '2025-02-28' },
  { id: 6, name: 'Dr. Mba', email: 'dr.mba@cnamgs.ga', role: 'doctor', status: 'active', phone: '+241 01 23 45 72', lastLogin: '2025-03-12' },
  { id: 7, name: 'Pharmacie Moderne', email: 'pharmacy.mod@cnamgs.ga', role: 'pharmacist', status: 'inactive', phone: '+241 01 23 45 73', lastLogin: '2025-03-01' },
  { id: 8, name: 'Super Admin', email: 'superadmin@cnamgs.ga', role: 'admin', status: 'active', phone: '+241 01 23 45 74', lastLogin: '2025-03-15' },
];

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState(mockUsers);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'patient',
  });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedRows(newSet);
  };

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  // Filtrage (inchangé)
  const filteredUsers = useMemo(() => {
    let filtered = users;
    if (roleFilter !== 'all') filtered = filtered.filter(u => u.role === roleFilter);
    if (statusFilter !== 'all') filtered = filtered.filter(u => u.status === statusFilter);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.name.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term) ||
        u.phone.includes(term)
      );
    }
    return filtered;
  }, [users, searchTerm, roleFilter, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(start, start + itemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  // Statistiques (inchangées)
  const stats = {
    total: users.length,
    patients: users.filter(u => u.role === 'patient').length,
    doctors: users.filter(u => u.role === 'doctor').length,
    pharmacists: users.filter(u => u.role === 'pharmacist').length,
    admins: users.filter(u => u.role === 'admin').length,
    active: users.filter(u => u.status === 'active').length,
    inactive: users.filter(u => u.status === 'inactive').length,
  };

  const statsCards = [
    { title: 'Total utilisateurs', value: stats.total, icon: People, color: '#4361ee', gradient: 'linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)', bg: 'white' },
    { title: 'Patients', value: stats.patients, icon: Person, color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', bg: 'white' },
    { title: 'Médecins', value: stats.doctors, icon: Shield, color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', bg: 'white' },
    { title: 'Actifs', value: stats.active, icon: CheckCircle, color: '#06b6d4', gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', bg: 'white' },
  ];

  const getRoleBadge = (role) => {
    const config = {
      patient: { bg: 'primary', label: 'Patient', icon: <Person size={12} className="me-1" /> },
      doctor: { bg: 'success', label: 'Médecin', icon: <Shield size={12} className="me-1" /> },
      pharmacist: { bg: 'info', label: 'Pharmacien', icon: <Shield size={12} className="me-1" /> },
      admin: { bg: 'danger', label: 'Admin', icon: <Shield size={12} className="me-1" /> },
    };
    const { bg, label, icon } = config[role] || { bg: 'secondary', label: role, icon: null };
    return <Badge bg={bg} className="rounded-pill px-3 py-2 d-inline-flex align-items-center">{icon} {label}</Badge>;
  };

  const getStatusBadge = (status) => {
    if (status === 'active') return <Badge bg="success" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> Actif</Badge>;
    return <Badge bg="secondary" className="rounded-pill px-3 py-2"><XCircle size={12} className="me-1" /> Inactif</Badge>;
  };

  const handleAdd = () => {
    setFormData({ name: '', email: '', phone: '', role: 'patient' });
    setShowAddModal(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role,
    });
    setShowEditModal(true);
  };

  const handleToggleStatus = (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    setUsers(prev => prev.map(u => u.id === user.id ? { ...u, status: newStatus } : u));
    showToast(`Utilisateur ${user.name} ${newStatus === 'active' ? 'activé' : 'désactivé'}`, 'success');
  };

  const handleDelete = (user) => {
    if (window.confirm(`Supprimer définitivement ${user.name} ?`)) {
      setUsers(prev => prev.filter(u => u.id !== user.id));
      showToast(`Utilisateur ${user.name} supprimé`, 'danger');
    }
  };

  const handleSaveAdd = () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      showToast('Veuillez remplir les champs obligatoires (nom, email)', 'danger');
      return;
    }
    setLoading(true);
    setTimeout(() => {
      const newId = Math.max(...users.map(u => u.id), 0) + 1;
      const newUser = {
        id: newId,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        status: 'active',
        lastLogin: new Date().toISOString().split('T')[0],
      };
      setUsers(prev => [...prev, newUser]);
      showToast('Utilisateur ajouté avec succès', 'success');
      setShowAddModal(false);
      setLoading(false);
    }, 800);
  };

  const handleSaveEdit = () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      showToast('Veuillez remplir les champs obligatoires (nom, email)', 'danger');
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, name: formData.name, email: formData.email, phone: formData.phone, role: formData.role } : u));
      showToast('Utilisateur modifié avec succès', 'success');
      setShowEditModal(false);
      setLoading(false);
    }, 800);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const items = [];
    for (let i = 1; i <= totalPages; i++) {
      items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
    }
    return (
      <div className="d-flex justify-content-end mt-3">
        <Pagination size="sm">
          <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
          {items}
          <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
        </Pagination>
      </div>
    );
  };

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={toast.show} onClose={() => setToast(prev => ({ ...prev, show: false }))} bg={toast.variant} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toast.variant === 'success' ? 'Succès' : toast.variant === 'danger' ? 'Erreur' : 'Information'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h4 className="main-title mb-0 d-flex align-items-center gap-2">
              <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                <People size={24} className="text-primary" />
              </div>
              Gestion des utilisateurs
            </h4>
            <p className="text-muted mb-0 mt-1">
              Gérez les comptes des patients, médecins, pharmaciens et administrateurs
            </p>
          </div>
          <Button variant="primary" onClick={handleAdd} className="rounded-pill px-4">
            <PlusCircle size={16} className="me-1" /> Ajouter un utilisateur
          </Button>
        </div>

        {/* Cartes de statistiques */}
        <Row className="g-3 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={6} lg={3} xs={6} key={idx}>
              <Card className="border-0 shadow-sm stats-card h-100" style={{ borderRadius: '16px', background: card.bg }}>
                <div className="card-gradient" style={{ background: card.gradient, height: '4px' }}></div>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="text-muted mb-1 small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>{card.title}</h6>
                      <h3 className="mb-0 fw-bold" style={{ fontSize: '1.6rem' }}>{card.value}</h3>
                    </div>
                    <div className="icon-container rounded-3 d-flex align-items-center justify-content-center" style={{ background: `${card.color}20`, width: '48px', height: '48px' }}>
                      <card.icon size={28} style={{ color: card.color }} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barre de recherche et filtres */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4 bg-white">
            <Row className="align-items-center g-3">
              <Col md={5} xs={6}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-0"><Search size={18} className="text-muted" /></InputGroup.Text>
                  <Form.Control type="text" placeholder="Rechercher par nom, email ou téléphone..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="border-0 bg-light" />
                </InputGroup>
              </Col>
              <Col md={3} xs={6}>
                <Form.Select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }} className="border-0 bg-light">
                  <option value="all">Tous rôles</option>
                  <option value="patient">Patients</option>
                  <option value="doctor">Médecins</option>
                  <option value="pharmacist">Pharmaciens</option>
                  <option value="admin">Administrateurs</option>
                </Form.Select>
              </Col>
              <Col md={2} xs={6}>
                <Form.Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="border-0 bg-light">
                  <option value="all">Tous statuts</option>
                  <option value="active">Actifs</option>
                  <option value="inactive">Inactifs</option>
                </Form.Select>
              </Col>
              <Col md={2} xs={6}>
                <Form.Select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="border-0 bg-light">
                  <option value={5}>5 par page</option>
                  <option value={10}>10 par page</option>
                  <option value={20}>20 par page</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tableau des utilisateurs */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
          <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
            <h5 className="fw-semibold mb-0">Liste des utilisateurs</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="bg-light">
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th className="d-none d-sm-table-cell">Téléphone</th>
                    <th className="d-none d-sm-table-cell">Rôle</th>
                    <th className="d-none d-sm-table-cell">Statut</th>
                 
                    <th className="d-none d-sm-table-cell">Actions</th>
                    <th className="d-sm-none">Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.length > 0 ? (
                    paginatedUsers.map(user => {
                      const isExpanded = expandedRows.has(user.id);
                      return (
                        <React.Fragment key={user.id}>
                          <tr>
                            <td data-label="Nom" className="fw-medium">{user.name}</td>
                            <td data-label="Email">{user.email}</td>
                            <td data-label="Téléphone" className="d-none d-sm-table-cell">{user.phone}</td>
                            <td data-label="Rôle" className="d-none d-sm-table-cell">{getRoleBadge(user.role)}</td>
                            <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(user.status)}</td>

                            <td data-label="Actions" className="d-none d-sm-table-cell">
                              <div className="d-flex gap-2">
                                <Button size="sm" variant="outline-primary" className="rounded-circle " style={{ width: '32px', height: '32px' }} onClick={() => handleEdit(user)} title="Modifier">
                                  <Pencil size={14} />
                                </Button>
                                <Button size="sm" variant={user.status === 'active' ? 'outline-secondary' : 'outline-success'} className="rounded-circle" style={{ width: '32px', height: '32px' }} onClick={() => handleToggleStatus(user)} title={user.status === 'active' ? 'Désactiver' : 'Activer'}>
                                  {user.status === 'active' ? <XCircle size={14} /> : <CheckCircle size={14} />}
                                </Button>
                                <Button size="sm" variant="outline-danger" className="rounded-circle" style={{ width: '32px', height: '32px' }} onClick={() => handleDelete(user)} title="Supprimer">
                                  <Trash size={14} />
                                </Button>
                              </div>
                            </td>
                            <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                              <Button variant="link" onClick={() => toggleRow(user.id)} className="p-0 text-secondary" style={{ textDecoration: 'none' }}>
                                {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                              </Button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="d-sm-none detail-row">
                              <td colSpan="8" className="p-3 bg-light">
                                <div className="row g-2">
                                  <div className="col-6"><strong>Téléphone :</strong> {user.phone}</div>
                                  <div className="col-6"><strong>Rôle :</strong> {getRoleBadge(user.role)}</div>
                                  <div className="col-6"><strong>Statut :</strong> {getStatusBadge(user.status)}</div>
                              
                                  <div className="col-12">
                                    <strong>Actions :</strong>
                                    <div className="d-flex gap-2 mt-1">
                                      <Button size="sm" variant="outline-primary" className="rounded-circle p-2" onClick={() => handleEdit(user)} title="Modifier">
                                        <Pencil size={14} />
                                      </Button>
                                      <Button size="sm" variant={user.status === 'active' ? 'outline-secondary' : 'outline-success'} className="rounded-circle p-2" onClick={() => handleToggleStatus(user)} title={user.status === 'active' ? 'Désactiver' : 'Activer'}>
                                        {user.status === 'active' ? <XCircle size={14} /> : <CheckCircle size={14} />}
                                      </Button>
                                      <Button size="sm" variant="outline-danger" className="rounded-circle p-2" onClick={() => handleDelete(user)} title="Supprimer">
                                        <Trash size={14} />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-5">
                        <People size={48} className="text-muted mb-3" />
                        <p className="text-muted">Aucun utilisateur trouvé</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            {renderPagination()}
          </Card.Body>
        </Card>
<br></br><br></br>
        {/* Modals (inchangés) */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title className="d-flex align-items-center gap-2"><PlusCircle size={20} /> Ajouter un utilisateur</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3"><Form.Label>Nom <span className="text-danger">*</span></Form.Label><Form.Control type="text" placeholder="Nom complet" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Email <span className="text-danger">*</span></Form.Label><Form.Control type="email" placeholder="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Téléphone</Form.Label><Form.Control type="text" placeholder="Téléphone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Rôle</Form.Label><Form.Select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}><option value="patient">Patient</option><option value="doctor">Médecin</option><option value="pharmacist">Pharmacien</option><option value="admin">Administrateur</option></Form.Select></Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} className="rounded-pill">Annuler</Button>
            <Button variant="primary" onClick={handleSaveAdd} disabled={loading} className="rounded-pill px-4">
              {loading ? <Spinner size="sm" className="me-1" /> : <CheckCircle size={16} className="me-1" />}
              {loading ? 'Ajout...' : 'Ajouter'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
          <Modal.Header closeButton className="bg-warning text-dark">
            <Modal.Title className="d-flex align-items-center gap-2"><Pencil size={20} /> Modifier l'utilisateur</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3"><Form.Label>Nom <span className="text-danger">*</span></Form.Label><Form.Control type="text" placeholder="Nom complet" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Email <span className="text-danger">*</span></Form.Label><Form.Control type="email" placeholder="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Téléphone</Form.Label><Form.Control type="text" placeholder="Téléphone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Rôle</Form.Label><Form.Select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}><option value="patient">Patient</option><option value="doctor">Médecin</option><option value="pharmacist">Pharmacien</option><option value="admin">Administrateur</option></Form.Select></Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)} className="rounded-pill">Annuler</Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={loading} className="rounded-pill px-4">
              {loading ? <Spinner size="sm" className="me-1" /> : <CheckCircle size={16} className="me-1" />}
              {loading ? 'Modification...' : 'Modifier'}
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .card-gradient {
            transition: height 0.2s ease;
          }
          .stats-card:hover .card-gradient {
            height: 6px;
          }
          .icon-container {
            transition: transform 0.2s ease;
          }
          .stats-card:hover .icon-container {
            transform: scale(1.05);
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
          @media (max-width: 767.98px) {
            .table td, .table th {
              padding: 0.5rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default UserManagement;